import { useRef, useState, type ReactElement } from "react";
import css from "./board.module.css";

/** Жест «обвести» (20.08, Саша): палец рисует дугу/окружность по доске —
 *  чипы операторов ([data-op] на glChip), чьи центры попали внутрь контура,
 *  выделяются. Тап по выделенному чипу и потянуть — групповой перенос-
 *  КОПИРОВАНИЕ: отпускание над игрой добавляет всех выделенных в её состав,
 *  исходные составы не меняются. Тап в пустоту — сброс выделения.
 *  Скролл не страдает: контур стартует только с горизонтального движения
 *  (touch-action: pan-y на поверхности), вертикальный старт уходит скроллу. */

const MIN_TRACE = 90;   // px суммарной длины контура — короче не считаем жестом
const MIN_BOX = 24;     // px меньшей стороны bbox контура
const START_SLOP = 12;  // px решения «рисуем или скроллим»

interface Pt { x: number; y: number }

/** Чёт-нечет: точка внутри замкнутого контура (контур замыкаем хордой). */
function inPoly(p: Pt, poly: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if (!a || !b) continue;
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

export function useLasso({ onCopy }: { onCopy: (opIds: string[], toGameId: string) => void }) {
  const [trace, setTrace] = useState<Pt[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const pen = useRef<{ pts: Pt[]; mode: "probe" | "draw" | "off" } | null>(null);
  const grp = useRef<{ ghost: HTMLDivElement; over: HTMLElement | null } | null>(null);
  const clear = () => setSelected(new Set());

  /* ── рисование контура (поверхность доски) ── */
  const down = (e: React.PointerEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("button, input, [data-op], [role='dialog']")) return;
    if (selected.size) clear(); // тап в пустоту при активном выделении — сброс
    pen.current = { pts: [{ x: e.clientX, y: e.clientY }], mode: "probe" };
  };
  const move = (e: React.PointerEvent) => {
    const p = pen.current; if (!p || p.mode === "off") return;
    const s = p.pts[0]; if (!s) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (p.mode === "probe") {
      if (Math.abs(dx) < START_SLOP && Math.abs(dy) < START_SLOP) return;
      if (Math.abs(dx) <= Math.abs(dy)) { pen.current = { pts: [], mode: "off" }; return; } // вертикаль → скролл
      p.mode = "draw";
      try { (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); } catch { /* синтетика */ }
    }
    const last = p.pts[p.pts.length - 1] ?? s;
    if (Math.hypot(e.clientX - last.x, e.clientY - last.y) > 5) { p.pts.push({ x: e.clientX, y: e.clientY }); setTrace([...p.pts]); }
  };
  const up = () => {
    const p = pen.current; pen.current = null; setTrace(null);
    if (!p || p.mode !== "draw") return;
    let len = 0;
    for (let i = 1; i < p.pts.length; i++) { const a = p.pts[i], b = p.pts[i - 1]; if (a && b) len += Math.hypot(a.x - b.x, a.y - b.y); }
    const xs = p.pts.map((q) => q.x), ys = p.pts.map((q) => q.y);
    if (len < MIN_TRACE || Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) < MIN_BOX) return;
    const hit = new Set<string>(); // ключ `gid:op` — подсвечивается чип конкретной игры, не тёзки на других
    document.querySelectorAll<HTMLElement>("[data-op]").forEach((el) => {
      const r = el.getBoundingClientRect();
      const gid = (el.closest("[data-gid]") as HTMLElement | null)?.dataset.gid;
      if (gid && inPoly({ x: r.x + r.width / 2, y: r.y + r.height / 2 }, p.pts)) hit.add(`${gid}:${el.dataset.op}`);
    });
    if (hit.size) setSelected(hit);
  };

  /* ── групповой drag-копирование с выделенного чипа ── */
  const groupDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    const g = document.createElement("div");
    if (css.dragGhost) g.className = css.dragGhost;
    g.textContent = `⧉ копирую: ${selected.size} оп.`;
    document.body.appendChild(g);
    grp.current = { ghost: g, over: null };
    const onMove = (ev: PointerEvent) => {
      const d = grp.current; if (!d) return;
      d.ghost.style.left = `${ev.clientX + 12}px`; d.ghost.style.top = `${ev.clientY - 36}px`;
      const tgt = (document.elementFromPoint(ev.clientX, ev.clientY)?.closest?.("[data-gid]") ?? null) as HTMLElement | null;
      if (d.over !== tgt) { if (css.dropHint) { d.over?.classList.remove(css.dropHint); tgt?.classList.add(css.dropHint); } d.over = tgt; }
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove); document.removeEventListener("pointerup", onUp);
      const d = grp.current; grp.current = null; if (!d) return;
      const gid = d.over?.dataset.gid ?? null;
      if (css.dropHint) d.over?.classList.remove(css.dropHint);
      d.ghost.remove();
      if (gid) { onCopy([...new Set([...selected].map((k) => k.slice(k.indexOf(":") + 1)))], gid); clear(); }
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const overlay: ReactElement | null = trace ? (
    <svg className={css.lassoTrace} aria-hidden="true">
      <polyline points={trace.map((q) => `${q.x},${q.y}`).join(" ")} />
    </svg>
  ) : null;

  return {
    selected, groupDown, overlay,
    surfaceProps: { onPointerDown: down, onPointerMove: move, onPointerUp: up, onPointerCancel: up, className: css.lassoSurface },
  };
}
